import React, { useEffect, useRef, useState } from 'react';
import styles from '../style/styles/Form.module.css';
import '../style/styles/global.scss';
import { useChatStateStore, useChatbotStore, useFormStore, useMessagesStore, useUserStore, useProductsStore } from '../zustand/store';
import { useFormSubmitted, useFormSubmittedFastfive, useLeadFormOpened } from '../services/queries/chatbotQueries';
import { getLeadFormAttemptId } from '../utils/leadFormAttempt';
import { getFormConfig } from '../data/formConfig';
import { useFormValidation, FormField, CheckboxGroup } from './Form/FormUtils';
import { openCenteredWindow, copyToClipboard } from '../utils/utils';

export const CollectionForm = (props) => {
    const { partnerType, partnerId, chatUserId } = useUserStore();
    const { products } = useProductsStore();
    const {
        chatContainerRef,
    } = props;

    const { setIsFormSubmitted, formAutoStartOnce, clearFormAutoStartOnce, fastfiveScenarioId, isChatOpen, isInConsoleTour } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { preAnswersObject, clearPreAnswersObject } = useFormStore();
    const { mutateAsync: formSubmitted } = useFormSubmitted();
    const { mutateAsync: formSubmittedFastfive } = useFormSubmittedFastfive();
    const { chatbot } = useChatbotStore();
    const { mutate: recordFormOpened } = useLeadFormOpened();
    const submitInFlight = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // Get form configuration based on clientId
    const formConfig = getFormConfig(partnerId);

    // Use custom hook for form validation
    const { formData, validList, allValid, handleInputChange, handleCheckboxChange, setFormData } = useFormValidation(formConfig);

    const [currentFormStep, setCurrentFormStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isFormStarted, setIsFormStarted] = useState(formAutoStartOnce || formConfig.triggerType === 'auto');

    useEffect(() => {
        if (formAutoStartOnce) {
            setIsFormStarted(true);
            // one-shot consumption: only auto-start once per trigger
            setTimeout(() => {
                clearFormAutoStartOnce();
            }, 1000);
        }
    }, [formAutoStartOnce]);

    useEffect(() => {
        if (!isFormStarted || !isChatOpen || isInConsoleTour || !partnerId || !chatUserId) return;
        recordFormOpened({
            formAttemptId: getLeadFormAttemptId(partnerId, chatUserId, fastfiveScenarioId),
            scenarioKey: fastfiveScenarioId,
        });
    }, [isFormStarted, isChatOpen, isInConsoleTour, partnerId, chatUserId, fastfiveScenarioId, recordFormOpened]);

    // Handle form start button click
    const handleFormStart = () => {
        setIsFormStarted(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isLastStep = currentFormStep === formConfig.fields.length - 1;
        if (!isLastStep || !allValid || isSubmitted || submitInFlight.current) {
            return; // Prevent implicit submit on intermediate steps
        }

        submitInFlight.current = true;
        setIsSubmitting(true);
        setSubmitError('');
        const formAttemptId = getLeadFormAttemptId(partnerId, chatUserId, fastfiveScenarioId);
        try {
            // Build clipboard text (template-aware, fallback to field summary)
            try {
                const template = formConfig?.submitClipboardTextTemplate;
                let clipboardText = '';
                if (template && typeof template === 'string') {
                    // Build placeholder map from form fields
                    const placeholderMap = formConfig.fields.reduce((acc, field) => {
                        acc[field.id] = formData?.[field.id] ?? '';
                        return acc;
                    }, {});
                    // Add productsList placeholder: "name price원" per line
                    const productsList = Array.isArray(products) && products.length > 0
                        ? products.map(p => {
                            const name = p?.name ?? '';
                            const price = typeof p?.price === 'number' ? p.price.toLocaleString() + '원' : (p?.price ?? '');
                            return `● ${name} ${price}`.trim();
                        }).join('\n')
                        : '';
                    placeholderMap['productsList'] = productsList;
                    clipboardText = template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
                        return (placeholderMap[key] ?? '');
                    });
                } else {
                    const lines = formConfig.fields.map(field => {
                        const label = field?.label || field?.id;
                        const value = formData?.[field.id] ?? '';
                        return `${label} ${value}`.trim();
                    });
                    clipboardText = lines.join('\n');
                }
                await copyToClipboard(clipboardText);
            } catch (_) {
                // Non-blocking if clipboard fails
            }

            let response;
            if (partnerType === 'fastfive') {
                const areaAnswer = Object.values(preAnswersObject).find(answer => answer?.key === 'area');
                const headcountAnswer = Object.values(preAnswersObject).find(answer => answer?.key === 'headcount');
                const parsedOfficeArea = Number(areaAnswer?.answer);
                const parsedUserCount = Number(headcountAnswer?.answer);
                response = await formSubmittedFastfive({
                    formAttemptId,
                    formData: formData,
                    // 시나리오 모드 진입 시 어떤 entry 로 들어왔는지 BE 에 전달 → 시나리오별 응답 comment 분기.
                    scenarioKey: fastfiveScenarioId || undefined,
                    officeArea: Number.isFinite(parsedOfficeArea) ? parsedOfficeArea : undefined,
                    userCount: Number.isFinite(parsedUserCount) ? parsedUserCount : undefined,
                });
            } else {
                if (chatbot.experimentalData?.find(data => data.key === 'csInquiry')?.value) {
                    openCenteredWindow(chatbot.experimentalData.find(data => data.key === 'csInquiry')?.value, 'kakaoChat', 480, 640);
                }
                response = await formSubmitted({
                    formAttemptId,
                    formData: formData,
                });
            }
            setMessages([{message: formData, type: 'form', isChatbot: false}]);
            if (response?.text?.[0]) setMessages([{ message: response.text[0], isChatbot: true }]);
            setIsSubmitted(true);
            setIsFormSubmitted('submitted');
            const targetWindow = window.parent;
            const formSubmittedState = {
                formSubmittedState: true,
                firstAnswer: preAnswersObject['0'],
                secondAnswer: preAnswersObject['1'],
                // p1/p2 는 시나리오마다 질문이 달라 상품 구분이 안 된다 (office 와 private 은 동일). 호스트 GA4 분기용.
                scenarioKey: fastfiveScenarioId || undefined,
                log: 'formSubmittedState in handleSubmit'
            }
            clearPreAnswersObject();
            targetWindow.postMessage(formSubmittedState, "*");
        } catch (_) {
            setSubmitError('제출을 완료하지 못했습니다. 입력 내용을 확인하고 다시 시도해 주세요.');
        } finally {
            submitInFlight.current = false;
            setIsSubmitting(false);
        }
    };  

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatContainerRef, currentFormStep])

    // Render form trigger button if needed
    const renderFormTrigger = () => {
        if (formConfig?.triggerType === 'button' && !isFormStarted) {
            return (
                <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <button 
                        type="button"
                        className={!isFormStarted ? styles.CollectionForm__button__started : styles.CollectionForm__button}
                        onClick={(e) => { e.stopPropagation(); handleFormStart(); }}
                    >
                        {formConfig.triggerButtonText}
                    </button>
                    {formConfig?.supportiveType?.tel && (
                        <button 
                            type="button"
                            style={{ backgroundColor: '#e9e9e9', border: 'none', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${formConfig.supportiveUrl.tel}`; }}
                        >
                            <img src='/img/units/tel-cs.png' alt="tel" width={16} height={16} />
                        </button>
                    )}
                </div>
            );
        }
        return null;
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter') {
            const isLastStep = currentFormStep === formConfig.fields.length - 1;
            if (!isLastStep) {
                e.preventDefault();
                const currentField = formConfig.fields[currentFormStep];
                const isCurrentFieldValid = validList[currentField?.id] || false;
                if (isCurrentFieldValid && !isSubmitted) {
                    setCurrentFormStep(currentFormStep + 1);
                }
            }
        }
    };

    // Render form fields based on current step
    const renderFormFields = () => {
        if (!isFormStarted && formConfig.triggerType === 'button') {
            return null;
        }

        const currentField = formConfig.fields[currentFormStep];
        const isLastStep = currentFormStep === formConfig.fields.length - 1;
        const isCurrentFieldValid = validList[currentField?.id] || false;

        return (
            <>
                {currentField && (
                    <FormField
                        field={currentField}
                        formData={formData}
                        validList={validList}
                        onInputChange={handleInputChange}
                        disabled={isSubmitted || isSubmitting}
                    />
                )}

                {!isLastStep && (
                    <button 
                        type="button"
                        className={isCurrentFieldValid && !isSubmitted ? styles.CollectionForm__button__small : styles.CollectionForm__button__small__disabled}
                        disabled={isSubmitted || isSubmitting || !isCurrentFieldValid}
                        onClick={(e) => { e.stopPropagation(); setCurrentFormStep(currentFormStep + 1); }}
                    >
                        다음
                    </button>
                )}

                {isLastStep && (
                    <>
                        <CheckboxGroup
                            checkboxGroup={formConfig.checkboxGroup}
                            formData={formData}
                            validList={validList}
                            onCheckboxChange={handleCheckboxChange}
                            disabled={isSubmitted || isSubmitting}
                        />
                        {!isSubmitted && (
                            <button 
                                type="submit" 
                                className={allValid && !isSubmitted ? styles.CollectionForm__button : styles.CollectionForm__button__disabled}
                                disabled={isSubmitted || isSubmitting || !allValid}
                                onClick={(e) => { e.stopPropagation(); }}
                            >
                                {formConfig.submitButton.text}
                            </button>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div className={styles.CollectionForm__container}>
            <div 
                className={!isFormStarted ? styles.CollectionForm__wrapper__started : styles.CollectionForm__wrapper}
            >
                <form 
                    onSubmit={handleSubmit}
                    onKeyDown={handleFormKeyDown}
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {renderFormTrigger()}
                    {renderFormFields()}
                    {submitError && <p role="alert">{submitError}</p>}
                </form>
            </div>
        </div>
    );
};

export const CompletedForm = (props) => {
    const { partnerType, partnerId } = useUserStore();
    const { 
        formData, 
        chatContainerRef,
        // clientId = partnerType === 'fastfive' ? 'fastfive' : 'csInquiry' 
     } = props;
    const formConfig = getFormConfig(partnerId);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatContainerRef])

    return (
        <div className={styles.CollectionForm__container}>
            <div className={styles.CollectionForm__wrapper}>
                <form>
                    {formConfig.fields.map(field => (
                        <div key={field.id} className={styles.CollectionForm__group}>
                            <label className={`${styles.CollectionForm__label} ${field.required ? styles.CollectionForm__required : ''}`}>
                                {field.label}
                            </label>
                            <input
                                type={field.type}
                                name={field.id}
                                className={styles.CollectionForm__input}
                                value={formData[field.id] || ''}
                                disabled={true}
                                autoComplete="off"
                            />
                        </div>
                    ))}

                    {formConfig.checkboxGroup.enabled && (
                        <div className={styles.CollectionForm__checkboxGroup}>
                            <label className={styles.CollectionForm__checkboxLabel}>
                                <input
                                    type="checkbox"
                                    name="all"
                                    checked={formData.all || false}
                                    disabled={true}
                                />
                                <span>{formConfig.checkboxGroup.title}</span>
                            </label>
                            {formConfig.checkboxGroup.checkboxes.map(checkbox => (
                                <label key={checkbox.id} className={styles.CollectionForm__checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name={checkbox.id}
                                        checked={formData[checkbox.id] || false}
                                        disabled={true}
                                    />
                                    <div>
                                        {checkbox.label.split(checkbox.link?.text || '').map((part, index) => (
                                            <span key={index}>
                                                {part}
                                                {index < checkbox.label.split(checkbox.link?.text || '').length - 1 && checkbox.link && (
                                                    <span 
                                                        className={styles.CollectionForm__link}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            window.open(checkbox.link.url, '_blank', 'noreferrer,noopener');
                                                        }}
                                                    >
                                                        {checkbox.link.text}
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </form>
            </div>
        </div>
    )
}
